/**
 * PATH: packages/email-hub/src/feature/email-layout/useEmailSyncAction.ts
 * FILE_ID: EMAIL-LAYOUT-SYNC-ACTION-001
 * PURPOSE: Preserve EmailLayout's manual and silent sync transaction.
 */

import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react'
import { EMAIL_CAPACITY_REFRESH_EVENT } from '../../_internal/hooks/useCapacityStatus'
import type { useCapacityPause } from '../../_internal/hooks/useCapacityPause'
import type { useToast } from '../../_internal/components/Toast'
import {
  newRequestId,
  reportClientIssue,
} from '../../_internal/services/integrationEvents'
import { QuotechApiError } from '../../_internal/services/quotechApi'
import type {
  useOutlookStatus,
  useSyncEmails,
} from '../hooks/useEmails'
import type { EmailSyncOptions } from './contracts'

type Sync = ReturnType<typeof useSyncEmails>['sync']
type Outlook = ReturnType<typeof useOutlookStatus>
type CapacityPause = ReturnType<typeof useCapacityPause>
type Toast = ReturnType<typeof useToast>['toast']

interface UseEmailSyncActionArgs {
  sync: Sync
  toast: Toast
  outlook: Outlook
  capacityPause: CapacityPause
  setLastSyncAt: Dispatch<SetStateAction<number | null>>
}

export function useEmailSyncAction({
  sync,
  toast,
  outlook,
  capacityPause,
  setLastSyncAt,
}: UseEmailSyncActionArgs) {
  const outlookRef = useRef(outlook)
  outlookRef.current = outlook
  const capacityPauseRef = useRef(capacityPause)
  capacityPauseRef.current = capacityPause

  // Manual Sync Now defaults to 30 days and force_full=true. Silent
  // auto-sync stays on the seven-day delta path.
  const DEFAULT_MANUAL_DAYS_BACK = 30
  const SILENT_DAYS_BACK = 7

  const handleSync = useCallback(async (opts?: EmailSyncOptions) => {
    const silent = !!opts?.silent
    if (capacityPauseRef.current.isPausedRef.current) {
      if (!silent) {
        const until = capacityPauseRef.current.pausedUntilRef.current
        const remaining = until
          ? Math.max(0, Math.ceil((until - Date.now()) / 1000))
          : 0
        toast(
          remaining > 0
            ? `System is busy. Try again in ~${remaining}s.`
            : 'System is busy. Try again shortly.',
          'warning',
        )
      }
      return
    }
    const forceFull =
      opts?.forceFull ?? (!opts?.silent && opts?.daysBack === undefined)
    const daysBack = Math.max(
      1,
      opts?.daysBack ?? (silent ? SILENT_DAYS_BACK : DEFAULT_MANUAL_DAYS_BACK),
    )
    const requestId = newRequestId('sync')
    const startedAt = Date.now()
    try {
      const result = await sync(daysBack, { forceFull })
      setLastSyncAt(Date.now())
      capacityPauseRef.current.clear()
      const fetched = result?.emails_fetched ?? 0
      const created = result?.emails_created ?? 0
      const queued = !!result?.queued
      const currentOutlook = outlookRef.current
      const baseCtx = {
        silent,
        days_back: daysBack,
        emails_fetched: fetched,
        emails_created: created,
        duration_ms: Date.now() - startedAt,
        outlook_connected: currentOutlook.connected,
        outlook_expired: currentOutlook.isExpired,
        queued,
      }

      if (queued) {
        const alreadyPending = !!result?.already_pending
        // A live worker, or a sync we just handed to one, is not an issue.
        // Recent issues was filling with "already running" and "queued"
        // rows every time the 7-day sync overlapped the backfill.
        if (!silent) {
          toast(
            alreadyPending
              ? 'Sync already running - refreshing shortly.'
              : 'Sync started - refreshing in a few seconds.',
            'info',
          )
        }
        return
      }

      if (
        fetched === 0
        && currentOutlook.connected === true
        && !currentOutlook.isExpired
        && daysBack >= 7
      ) {
        void reportClientIssue({
          source: 'client.email.sync',
          severity: 'warning',
          reason: 'no_fetch_over_window',
          message: `Sync ran but received 0 emails over the last ${daysBack} days. Outlook session may need reconnecting.`,
          requestId,
          context: baseCtx,
        })
        if (!silent) {
          toast(
            `Sync ran but received 0 emails over the last ${daysBack} days. Try reconnecting Outlook.`,
            'warning',
          )
        }
      } else if (created > 0) {
        void reportClientIssue({
          source: 'client.email.sync',
          severity: 'info',
          reason: 'sync_success',
          message: `Sync added ${created} new email(s) (fetched ${fetched}, last ${daysBack} days).`,
          requestId,
          context: baseCtx,
        })
        if (silent) {
          toast(
            `${created} new ${created === 1 ? 'email' : 'emails'} arrived.`,
            'success',
          )
        } else {
          toast(
            `Synced ${created} new ${created === 1 ? 'email' : 'emails'} (${fetched} fetched, last ${daysBack} days).`,
            'success',
          )
        }
      } else {
        void reportClientIssue({
          source: 'client.email.sync',
          severity: 'info',
          reason: 'already_synced',
          message: `Sync ran (last ${daysBack} days, ${fetched} fetched, 0 new).`,
          requestId,
          context: baseCtx,
        })
        if (!silent) {
          toast(
            fetched > 0
              ? `Inbox is up to date (last ${daysBack} days, ${fetched} fetched).`
              : `Inbox is up to date (last ${daysBack} days).`,
            'info',
          )
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed'
      const currentOutlook = outlookRef.current
      const isCapacity429 =
        err instanceof QuotechApiError
        && err.status === 429
        && err.code === 'capacity_rejected'
      const retryAfter =
        err instanceof QuotechApiError ? err.retryAfterSeconds : undefined
      const isTransientNetwork =
        !isCapacity429 && (
          (err instanceof TypeError
            && /failed to fetch|networkerror|load failed/i.test(msg))
          || (
            typeof DOMException !== 'undefined'
            && err instanceof DOMException
            && err.name === 'AbortError'
          )
          || (typeof navigator !== 'undefined' && navigator.onLine === false)
        )
      const capacityBody =
        isCapacity429 && err instanceof QuotechApiError
          ? (err.details as Record<string, unknown> | undefined)
          : undefined
      const trippedNames = Array.isArray(capacityBody?.tripped_signals)
        ? (capacityBody.tripped_signals as unknown[]).map(String)
        : []
      const capacitySignals =
        capacityBody?.signals && typeof capacityBody.signals === 'object'
          ? (capacityBody.signals as Record<string, number | null>)
          : {}
      const CAPACITY_LABELS: Record<string, string> = {
        load_per_cpu: 'CPU',
        queue_depth: 'Job queue',
        redis_memory_pct: 'Redis memory',
      }
      const fmtCapacity = (
        key: string,
        value: number | null | undefined,
      ) => {
        const label = CAPACITY_LABELS[key] ?? key
        if (value == null) return label
        if (key === 'load_per_cpu') {
          return `${label} ${Number(value).toFixed(1)}×`
        }
        if (key === 'redis_memory_pct') {
          return `${label} ${Math.round(Number(value))}%`
        }
        return `${label} ${Number(value).toLocaleString()}`
      }
      const busyText = trippedNames
        .map((key) => fmtCapacity(key, capacitySignals[key]))
        .join(', ')

      if (isCapacity429) {
        try {
          window.dispatchEvent(new Event(EMAIL_CAPACITY_REFRESH_EVENT))
        } catch {
          // Older bridges without the Event constructor use the 30s poll.
        }
        capacityPauseRef.current.pauseFor(retryAfter)
      }

      if (isCapacity429) {
        if (!silent) {
          const lead = busyText
            ? `System is busy (${busyText}).`
            : 'System is busy.'
          toast(
            retryAfter
              ? `${lead} Try again in ~${retryAfter}s.`
              : `${lead} Try again shortly.`,
            'warning',
          )
        }
      } else if (isTransientNetwork) {
        if (!silent) {
          toast(
            'Couldn’t reach the email server - check your connection and try again.',
            'warning',
          )
        }
      } else if (
        !silent
        || (currentOutlook.connected && !currentOutlook.isExpired)
      ) {
        toast(`Email sync failed: ${msg}`, 'error')
      }
      void reportClientIssue({
        source: 'client.email.sync',
        severity: isCapacity429
          ? 'warning'
          : (isTransientNetwork && silent ? 'warning' : 'error'),
        reason: isCapacity429
          ? 'capacity_rejected'
          : isTransientNetwork
            ? (silent ? 'auto_sync_network_transient' : 'manual_sync_network')
            : (silent ? 'auto_sync_failed' : 'manual_sync_failed'),
        message: `Email sync failed: ${msg}`,
        requestId,
        context: {
          silent,
          days_back: daysBack,
          outlook_connected: currentOutlook.connected,
          outlook_expired: currentOutlook.isExpired,
          outlook_reachable: currentOutlook.reachable,
          duration_ms: Date.now() - startedAt,
          retry_after_seconds: retryAfter,
          capacity_reason:
            typeof err === 'object' && err !== null && 'code' in err
              ? String((err as { code?: unknown }).code ?? '')
              : undefined,
          capacity_tripped: trippedNames.length ? trippedNames : undefined,
          capacity_signals:
            Object.keys(capacitySignals).length ? capacitySignals : undefined,
        },
        detail: err instanceof Error && err.stack ? err.stack : undefined,
      })
      if (!isCapacity429 && !isTransientNetwork) {
        void currentOutlook.check()
      }
    }
  }, [sync, toast, setLastSyncAt])

  return { handleSync, capacityPauseRef }
}
